import React, { useState } from "react";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  getEmployees, getAttendance, getAttendanceSummary, bulkUpsertAttendance,
  getAttendanceAnalytics, getOnLeave,
  getRegularizations, submitRegularization, reviewRegularization,
  punchIn, punchOut, getAttendanceConfig, getPunchPhotoUrl,
  getShifts, createShift, updateShift, deleteShift, getShiftRoster, assignShiftRoster,
} from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { DateInput } from "../components/DateInput";
import CameraCaptureModal from "../components/CameraCaptureModal";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { TIME_SLOTS } from "../utils/timeSlots";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  present:        { label: "Present",   color: "#16a34a", bg: "#dcfce7" },
  absent:         { label: "Absent",    color: "#dc2626", bg: "#fee2e2" },
  half_day:       { label: "Half Day",  color: "#d97706", bg: "#fef9c3" },
  work_from_home: { label: "WFH",       color: "#2563eb", bg: "#dbeafe" },
  on_leave:       { label: "On Leave",  color: "#7c3aed", bg: "#ede9fe" },
};

// Statuses an employee may self-select when punching. Absent and On Leave are
// deliberately excluded — approving leave now writes on_leave attendance rows
// automatically, so letting someone also self-declare it produced two sources
// of truth for the same day that could disagree.
const PUNCHABLE_STATUSES = ["present", "work_from_home", "half_day"];

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

type ViewTab = "my" | "team" | "shifts" | "summary" | "analytics" | "corrections";

// ── CSV helpers ───────────────────────────────────────────────────────────────
function downloadCSV(rows: string[][], filename: string) {
  const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  // Leading BOM (bug-17) — without it, Excel opens this UTF-8 file as Windows-1252 and
  // renders any non-ASCII character as mojibake.
  a.href = "data:text/csv;charset=utf-8," + encodeURIComponent("\uFEFF" + csv);
  a.download = filename;
  a.click();
}

// ── Location label ────────────────────────────────────────────────────────────
// A punch can carry real GPS coordinates and still have no office geofence to be
// measured against — the server calls that "captured". Those rows used to render
// as "Manual", which mislabelled a GPS punch and dropped the coordinates entirely.
// Anything holding a lat/lng now links to the map; only a genuinely location-less
// row reads as manual.
const mapsUrl = (lat: number, lng: number) => `https://maps.google.com/?q=${lat},${lng}`;

function LocationLabel({ record, compact = false }: { record: any; compact?: boolean }) {
  const lat = record?.check_in_lat;
  const lng = record?.check_in_lng;
  const status = record?.location_status;

  const withMap = (text: string, title: string, color: string) =>
    lat != null && lng != null
      ? <a href={mapsUrl(lat, lng)} target="_blank" rel="noreferrer" title={`${title} — view on map`}
           style={{ color, textDecoration: "none" }}>{text}</a>
      : <span title={title} style={{ color }}>{text}</span>;

  if (status === "office") return withMap(compact ? "🏢" : "🏢 Office", "In office", "#16a34a");
  if (status === "wfh") return <span title="Work from home" style={{ color: "#2563eb" }}>{compact ? "🏠" : "🏠 WFH"}</span>;
  if (status === "outside") return withMap(compact ? "📍" : "📍 Outside — map", "Outside office", "#d97706");
  if (status === "captured") return withMap(compact ? "📍" : "📍 Location — map", "Location captured", "#2563eb");
  if (status === "denied") return <span style={{ color: "#94a3b8" }} title="Location permission denied">{compact ? "⚠️" : "⚠️ No location"}</span>;
  // Punches recorded before "captured" existed were stored as "manual" even when
  // they carried coordinates — show those on the map too rather than hiding them.
  if (lat != null && lng != null) return withMap(compact ? "📍" : "📍 Location — map", "Location captured", "#2563eb");
  return compact ? null : <span style={{ color: "#cbd5e1" }}>Manual</span>;
}

// ── Who's In Today ────────────────────────────────────────────────────────────
function WhosInToday({ date, employees }: { date: string; employees: any[] }) {
  const { data: dayRecords = [] } = useQuery({
    queryKey: ["attendance-day-widget", date],
    queryFn: () => getAttendance({ year: parseInt(date.slice(0, 4)), month: parseInt(date.slice(5, 7)) }),
    select: (data: any[]) => data.filter((r: any) => r.date === date),
  });

  const counts: Record<string, number> = { present: 0, absent: 0, half_day: 0, work_from_home: 0, on_leave: 0 };
  (dayRecords as any[]).forEach((r: any) => { if (r.status in counts) counts[r.status]++; });
  const total = employees.length;
  const marked = (dayRecords as any[]).length;
  const unmarked = total - marked;

  return (
    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10 }}>Today's Overview — {date}</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {Object.entries(STATUS_CONFIG).map(([k, v]) => counts[k] > 0 && (
          <div key={k} style={{ textAlign: "center", minWidth: 56 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: v.color }}>{counts[k]}</div>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{v.label}</div>
          </div>
        ))}
        {unmarked > 0 && (
          <div style={{ textAlign: "center", minWidth: 56 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#94a3b8" }}>{unmarked}</div>
            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>Not Marked</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const { data: analytics }: any = useQuery({ queryKey: ["attendance-analytics"], queryFn: getAttendanceAnalytics });
  if (!analytics) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading analytics…</div>;

  const STATUS_COLORS: Record<string, string> = {
    present: "#16a34a", work_from_home: "#2563eb", half_day: "#d97706",
    absent: "#dc2626", on_leave: "#7c3aed",
  };

  return (
    <div style={{ display: "grid", gap: 24 }}>
      {/* Today summary */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
          <div key={k} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 20px", textAlign: "center", flex: "1 1 100px" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: v.color }}>{analytics.today[k] || 0}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Today {v.label}</div>
          </div>
        ))}
      </div>

      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Attendance rate trend */}
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>Attendance Rate — Last 6 Months</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={analytics.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: any) => [`${v}%`, "Attendance Rate"]} />
              <Line type="monotone" dataKey="attendance_rate" stroke={NAVY} strokeWidth={2} dot={{ r: 4, fill: NAVY }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Current month distribution pie */}
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>This Month's Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={analytics.current_month_distribution} dataKey="count" nameKey="status"
                cx="50%" cy="50%" outerRadius={70} label={({ status, percent }: any) =>
                  `${STATUS_CONFIG[status]?.label || status} ${(percent * 100).toFixed(0)}%`}>
                {analytics.current_month_distribution.map((d: any, i: number) => (
                  <Cell key={i} fill={STATUS_COLORS[d.status] || "#94a3b8"} />
                ))}
              </Pie>
              <Tooltip formatter={(v: any, name: any) => [v, STATUS_CONFIG[name]?.label || name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Dept absenteeism */}
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>Absence Rate by Department</h3>
          {analytics.dept_absenteeism.length === 0
            ? <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No data yet</div>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.dept_absenteeism} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tickFormatter={v => `${v}%`} domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={90} />
                  <Tooltip formatter={(v: any) => [`${v}%`, "Absence Rate"]} />
                  <Bar dataKey="absence_rate" fill="#dc2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
        </div>

        {/* Top absentees */}
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>Top Absentees This Month</h3>
          {analytics.top_absentees.length === 0
            ? <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No absences recorded</div>
            : (
              <div style={{ display: "grid", gap: 8 }}>
                {analytics.top_absentees.map((emp: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: i === 0 ? "#fee2e2" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: i === 0 ? "#dc2626" : "#64748b", flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{emp.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>{emp.absent_days}d</div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

// ── Corrections Tab ───────────────────────────────────────────────────────────
function CorrectionsTab({ isManager }: { isManager: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: "", requested_status: "present", requested_check_in: "", requested_check_out: "", reason: "" });

  const { data: regs = [] } = useQuery({ queryKey: ["regularizations"], queryFn: getRegularizations });

  const submitMut = useMutation({
    mutationFn: submitRegularization,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["regularizations"] }); setShowForm(false); setForm({ date: "", requested_status: "present", requested_check_in: "", requested_check_out: "", reason: "" }); toast("Correction request submitted"); },
    onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error"),
  });
  const reviewMut = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "approve" | "reject" }) => reviewRegularization(id, action),
    onSuccess: (_, { action }) => { qc.invalidateQueries({ queryKey: ["regularizations"] }); qc.invalidateQueries({ queryKey: ["attendance-records"] }); toast(action === "approve" ? "Approved — attendance updated" : "Rejected"); },
    onError: () => toast("Failed", "error"),
  });

  const pending = (regs as any[]).filter((r: any) => r.status === "pending");
  const reviewed = (regs as any[]).filter((r: any) => r.status !== "pending");

  return (
    <div>
      <LoadingOverlay active={submitMut.isPending || reviewMut.isPending} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: "#64748b" }}>{pending.length} pending</span>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={primaryBtnStyle}>+ Request Correction</button>
        )}
      </div>

      {showForm && (
        <div style={{ background: "white", border: "1.5px solid #bfdbfe", borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 14px", color: NAVY, fontSize: 14 }}>Attendance Correction Request</h4>
          <div style={{ display: "grid", gap: 12 }}>
            <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Date *</label>
                <DateInput value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}
                  max={new Date().toISOString().slice(0, 10)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Correct Status *</label>
                <select value={form.requested_status} onChange={e => setForm(f => ({...f, requested_status: e.target.value}))} style={inputStyle}>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Check-in Time</label>
                <select value={form.requested_check_in} onChange={e => setForm(f => ({...f, requested_check_in: e.target.value}))} style={{ ...inputStyle, background: "white" }}>
                  <option value="">Select...</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Check-out Time</label>
                <select value={form.requested_check_out} onChange={e => setForm(f => ({...f, requested_check_out: e.target.value}))} style={{ ...inputStyle, background: "white" }}>
                  <option value="">Select...</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Reason *</label>
              <textarea value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                placeholder="e.g. Forgot to mark, system was down, WFH not recorded…"
                rows={2} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            {(() => {
              // The Submit button was silently disabled with no indication of why
              // required fields were missing (bug-62) — spell it out inline.
              const missing = [!form.date && "Date", !form.reason.trim() && "Reason"].filter(Boolean) as string[];
              return (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={() => submitMut.mutate(form)} disabled={missing.length > 0 || submitMut.isPending}
                    style={primaryBtnStyle}>{submitMut.isPending ? "Submitting…" : "Submit Request"}</button>
                  <button onClick={() => setShowForm(false)} style={{ ...primaryBtnStyle, background: "white", color: "#64748b", border: "1px solid #e2e8f0" }}>Cancel</button>
                  {missing.length > 0 && (
                    <span style={{ fontSize: 12, color: "#dc2626" }}>{missing.join(" and ")} {missing.length > 1 ? "are" : "is"} required.</span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {pending.length === 0 && reviewed.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 36 }}>📋</div>
          <div style={{ marginTop: 10, fontSize: 14 }}>No correction requests yet.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Use "Request Correction" to fix a past attendance entry.</div>
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#475569" }}>Pending ({pending.length})</h4>
          {pending.map((r: any) => (
            <div key={r.id}><RegCard reg={r} isManager={isManager} onAction={(action) => reviewMut.mutate({ id: r.id, action })} loading={reviewMut.isPending} /></div>
          ))}
        </div>
      )}
      {reviewed.length > 0 && (
        <div>
          <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#475569" }}>Reviewed ({reviewed.length})</h4>
          {reviewed.map((r: any) => (
            <div key={r.id}><RegCard reg={r} isManager={false} /></div>
          ))}
        </div>
      )}
    </div>
  );
}

function RegCard({ reg, isManager, onAction, loading }: { reg: any; isManager: boolean; onAction?: (a: "approve" | "reject") => void; loading?: boolean }) {
  const sc = STATUS_CONFIG[reg.requested_status] || STATUS_CONFIG.present;
  const statusColor = reg.status === "approved" ? "#16a34a" : reg.status === "rejected" ? "#dc2626" : "#d97706";
  const statusBg = reg.status === "approved" ? "#dcfce7" : reg.status === "rejected" ? "#fee2e2" : "#fef9c3";
  return (
    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 18px", marginBottom: 8 }}>
      <div className="card-split-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3 }}>
            {isManager && <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{reg.employee_name}</span>}
            <span style={{ fontSize: 12, color: "#64748b" }}>{reg.date}</span>
            <span style={{ background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 10 }}>→ {sc.label}</span>
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{reg.reason}</div>
          {(reg.requested_check_in || reg.requested_check_out) && (
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
              {reg.requested_check_in && `In: ${reg.requested_check_in.slice(0, 5)}`}
              {reg.requested_check_out && ` · Out: ${reg.requested_check_out.slice(0, 5)}`}
            </div>
          )}
          {(reg.has_check_in_photo || reg.has_check_out_photo) && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {reg.has_check_in_photo && (
                <a href={getPunchPhotoUrl(reg.user_id, reg.date, "in")} target="_blank" rel="noreferrer" title="Check-in photo (opens full size)">
                  <img src={getPunchPhotoUrl(reg.user_id, reg.date, "in")} alt="Check-in punch" style={{ height: 64, borderRadius: 6, border: "1px solid #e2e8f0" }} />
                </a>
              )}
              {reg.has_check_out_photo && (
                <a href={getPunchPhotoUrl(reg.user_id, reg.date, "out")} target="_blank" rel="noreferrer" title="Check-out photo (opens full size)">
                  <img src={getPunchPhotoUrl(reg.user_id, reg.date, "out")} alt="Check-out punch" style={{ height: 64, borderRadius: 6, border: "1px solid #e2e8f0" }} />
                </a>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ background: statusBg, color: statusColor, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>{reg.status}</span>
          {isManager && reg.status === "pending" && onAction && (
            <>
              <button onClick={() => onAction("approve")} disabled={loading}
                style={{ background: "#dcfce7", color: "#16a34a", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Approve</button>
              <button onClick={() => onAction("reject")} disabled={loading}
                style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Reject</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shifts tab (manager: shift CRUD + weekly roster) ──────────────────────────
function mondayOf(d: Date): Date {
  const out = new Date(d);
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
}
function iso(d: Date): string { return d.toISOString().slice(0, 10); }
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EMPTY_SHIFT_FORM = { name: "", start_time: "09:00", end_time: "18:00", break_minutes: "60", grace_minutes: "15" };

function ShiftsTab({ employees }: { employees: any[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const { data: shifts = [] } = useQuery({ queryKey: ["shifts"], queryFn: () => getShifts(true) });
  const activeShifts = (shifts as any[]).filter((s: any) => s.is_active);

  // ── Shift CRUD ──
  const [shiftForm, setShiftForm] = useState<any>(null); // null = closed, {id?} = create/edit
  const saveShiftMut = useMutation({
    mutationFn: (f: any) => f.id ? updateShift(f.id, f) : createShift(f),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["shifts"] }); setShiftForm(null); toast("Shift saved"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save shift", "error"),
  });
  const deleteShiftMut = useMutation({
    mutationFn: deleteShift,
    onSuccess: (res: any) => { qc.invalidateQueries({ queryKey: ["shifts"] }); toast(res.deactivated ? "Shift has roster history — deactivated instead" : "Shift deleted"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
  });

  // ── Roster ──
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const { data: roster = [] } = useQuery({
    queryKey: ["shift-roster", iso(weekStart)],
    queryFn: () => getShiftRoster({ start: iso(weekDays[0]), end: iso(weekDays[6]) }),
  });
  const rosterMap: Record<string, any> = {};
  (roster as any[]).forEach((a: any) => { rosterMap[`${a.user_id}|${a.date}`] = a.shift; });

  // ── Assign form ──
  const [assign, setAssign] = useState({ user_ids: [] as number[], shift_id: "" as string, start_date: iso(weekDays[0]), end_date: iso(weekDays[6]), weekdays: [0, 1, 2, 3, 4] as number[] });
  const assignMut = useMutation({
    mutationFn: () => assignShiftRoster({
      user_ids: assign.user_ids,
      shift_id: assign.shift_id === "" ? null : parseInt(assign.shift_id),
      start_date: assign.start_date, end_date: assign.end_date, weekdays: assign.weekdays,
    }),
    onSuccess: (res: any) => { qc.invalidateQueries({ queryKey: ["shift-roster"] }); toast(`Roster updated — ${res.saved} assigned, ${res.cleared} cleared`); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to assign", "error"),
  });

  const inputSm: React.CSSProperties = { padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box" };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <LoadingOverlay active={saveShiftMut.isPending || deleteShiftMut.isPending || assignMut.isPending} />

      {/* ── Shift definitions ── */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: NAVY, fontSize: 15 }}>Shift Definitions</h3>
          {!shiftForm && <button onClick={() => setShiftForm({ ...EMPTY_SHIFT_FORM })} style={primaryBtnStyle}>+ New Shift</button>}
        </div>

        {(shifts as any[]).length === 0 && !shiftForm && (
          <div style={{ color: "#94a3b8", fontSize: 13, padding: "16px 0" }}>No shifts yet. Create one to start rostering.</div>
        )}

        {(shifts as any[]).map((sh: any) => (
          <div key={sh.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: sh.is_active ? "#1e293b" : "#94a3b8" }}>{sh.name}</span>
            <span style={{ fontSize: 13, color: "#475569" }}>{sh.start_time} – {sh.end_time}</span>
            {sh.is_overnight && <span style={{ background: "#ede9fe", color: "#7c3aed", fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 10 }}>overnight</span>}
            <span style={{ fontSize: 12, color: "#94a3b8" }}>break {sh.break_minutes}m · grace {sh.grace_minutes}m</span>
            {!sh.is_active && <span style={{ background: "#f1f5f9", color: "#94a3b8", fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 10 }}>inactive</span>}
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button onClick={() => setShiftForm({ id: sh.id, name: sh.name, start_time: sh.start_time, end_time: sh.end_time, break_minutes: String(sh.break_minutes), grace_minutes: String(sh.grace_minutes), is_active: sh.is_active })}
                style={{ background: "#eff6ff", color: NAVY, border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Edit</button>
              <button onClick={async () => {
                if (await confirm(`Delete the "${sh.name}" shift? If it is already used in a roster it is deactivated instead, so existing rows stay intact.`,
                  { title: "Delete shift", confirmLabel: "Delete" })) deleteShiftMut.mutate(sh.id);
              }}
                style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Delete</button>
            </span>
          </div>
        ))}

        {shiftForm && (
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, marginTop: 12 }}>
            <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Name *</label>
                <input value={shiftForm.name} onChange={e => setShiftForm((f: any) => ({ ...f, name: e.target.value }))} placeholder="e.g. Morning, Night" style={{ ...inputSm, width: "100%" }} />
              </div>
              <div>
                <label style={labelStyle}>Start</label>
                <input type="time" value={shiftForm.start_time} onChange={e => setShiftForm((f: any) => ({ ...f, start_time: e.target.value }))} style={{ ...inputSm, width: "100%" }} />
              </div>
              <div>
                <label style={labelStyle}>End</label>
                <input type="time" value={shiftForm.end_time} onChange={e => setShiftForm((f: any) => ({ ...f, end_time: e.target.value }))} style={{ ...inputSm, width: "100%" }} />
              </div>
              <div>
                <label style={labelStyle}>Break (min)</label>
                <input type="number" value={shiftForm.break_minutes} onChange={e => setShiftForm((f: any) => ({ ...f, break_minutes: e.target.value }))} style={{ ...inputSm, width: "100%" }} />
              </div>
              <div>
                <label style={labelStyle}>Grace (min)</label>
                <input type="number" value={shiftForm.grace_minutes} onChange={e => setShiftForm((f: any) => ({ ...f, grace_minutes: e.target.value }))} style={{ ...inputSm, width: "100%" }} />
              </div>
            </div>
            {shiftForm.end_time <= shiftForm.start_time && (
              <div style={{ fontSize: 12, color: "#7c3aed", marginBottom: 10 }}>🌙 End is at/before start — this will be saved as an overnight shift crossing midnight.</div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => saveShiftMut.mutate({ ...shiftForm, break_minutes: parseInt(shiftForm.break_minutes || "0"), grace_minutes: parseInt(shiftForm.grace_minutes || "0") })}
                disabled={!shiftForm.name.trim() || saveShiftMut.isPending} style={primaryBtnStyle}>
                {saveShiftMut.isPending ? "Saving…" : shiftForm.id ? "Update Shift" : "Create Shift"}
              </button>
              {shiftForm.id && (
                <label style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={shiftForm.is_active !== false} onChange={e => setShiftForm((f: any) => ({ ...f, is_active: e.target.checked }))} /> Active
                </label>
              )}
              <button onClick={() => setShiftForm(null)} style={{ ...primaryBtnStyle, background: "white", color: "#64748b", border: "1px solid #e2e8f0" }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Bulk assign ── */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: "0 0 12px", color: NAVY, fontSize: 15 }}>Assign Roster</h3>
        <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Employees *</label>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, maxHeight: 160, overflowY: "auto", padding: "6px 10px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: NAVY, padding: "3px 0", cursor: "pointer" }}>
                <input type="checkbox" checked={assign.user_ids.length === employees.length && employees.length > 0}
                  onChange={e => setAssign(a => ({ ...a, user_ids: e.target.checked ? employees.map((emp: any) => emp.id) : [] }))} />
                Select all ({employees.length})
              </label>
              {employees.map((emp: any) => (
                <label key={emp.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={assign.user_ids.includes(emp.id)}
                    onChange={e => setAssign(a => ({ ...a, user_ids: e.target.checked ? [...a.user_ids, emp.id] : a.user_ids.filter(id => id !== emp.id) }))} />
                  {emp.name} <span style={{ color: "#94a3b8", fontSize: 11 }}>{emp.department || ""}</span>
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
            <div>
              <label style={labelStyle}>Shift *</label>
              <select value={assign.shift_id} onChange={e => setAssign(a => ({ ...a, shift_id: e.target.value }))} style={{ ...inputSm, width: "100%" }}>
                <option value="">— Clear assignment —</option>
                {activeShifts.map((sh: any) => <option key={sh.id} value={sh.id}>{sh.name} ({sh.start_time}–{sh.end_time})</option>)}
              </select>
            </div>
            <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={labelStyle}>From</label>
                <DateInput value={assign.start_date} onChange={e => setAssign(a => ({ ...a, start_date: e.target.value }))} style={{ ...inputSm, width: "100%" }} />
              </div>
              <div>
                <label style={labelStyle}>To</label>
                <DateInput value={assign.end_date} onChange={e => setAssign(a => ({ ...a, end_date: e.target.value }))} style={{ ...inputSm, width: "100%" }} />
              </div>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <label style={labelStyle}>Days of week</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {([["All", [0, 1, 2, 3, 4, 5, 6]], ["Weekdays", [0, 1, 2, 3, 4]], ["None", []]] as [string, number[]][]).map(([lbl, days]) => (
                    <button key={lbl} onClick={() => setAssign(a => ({ ...a, weekdays: days }))}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: NAVY, textDecoration: "underline" }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {WEEKDAY_LABELS.map((d, i) => (
                  <button key={d} onClick={() => setAssign(a => ({ ...a, weekdays: a.weekdays.includes(i) ? a.weekdays.filter(w => w !== i) : [...a.weekdays, i] }))}
                    style={{ padding: "5px 10px", borderRadius: 14, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      border: `1.5px solid ${assign.weekdays.includes(i) ? NAVY : "#e2e8f0"}`,
                      background: assign.weekdays.includes(i) ? "#eff6ff" : "white",
                      color: assign.weekdays.includes(i) ? NAVY : "#94a3b8" }}>{d}</button>
                ))}
              </div>
            </div>
            {(() => {
              // The button used to sit greyed out with no reason given — the same
              // complaint bug-62 fixed on the corrections form above. It also never
              // said that the weekday chips SCOPE the action, so "Clear Assignments"
              // with the default Mon–Fri silently left weekend rows behind.
              const missing = [
                assign.user_ids.length === 0 && "Select at least one employee",
                assign.weekdays.length === 0 && "Select at least one day of the week",
              ].filter(Boolean) as string[];
              const clearing = assign.shift_id === "";
              const label = clearing ? "Clear Assignments" : "Apply to Roster";
              const scope = assign.weekdays.length === 7
                ? "every day"
                : `${assign.weekdays.length} of 7 days`;
              return (
                <div>
                  <button onClick={() => assignMut.mutate()}
                    disabled={missing.length > 0 || assignMut.isPending}
                    style={{ ...primaryBtnStyle, width: "100%", opacity: missing.length > 0 ? 0.5 : 1 }}>
                    {assignMut.isPending ? "Applying…" : `${label} · ${scope}`}
                  </button>
                  {missing.length > 0 && (
                    <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>{missing.join(" · ")}</div>
                  )}
                  {missing.length === 0 && clearing && assign.weekdays.length < 7 && (
                    <div style={{ fontSize: 12, color: "#d97706", marginTop: 6 }}>
                      Only the selected days will be cleared — assignments on the other days stay.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Week grid ── */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, color: NAVY, fontSize: 15 }}>Week of {iso(weekDays[0])}</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; })} style={{ ...primaryBtnStyle, background: "white", color: "#475569", border: "1px solid #e2e8f0" }}>← Prev</button>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} style={{ ...primaryBtnStyle, background: "white", color: "#475569", border: "1px solid #e2e8f0" }}>This week</button>
            <button onClick={() => setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; })} style={{ ...primaryBtnStyle, background: "white", color: "#475569", border: "1px solid #e2e8f0" }}>Next →</button>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={thStyle}>Employee</th>
                {weekDays.map((d, i) => <th key={i} style={{ ...thStyle, textAlign: "center" }}>{WEEKDAY_LABELS[i]}<br /><span style={{ fontWeight: 400, fontSize: 10, color: "#94a3b8" }}>{iso(d).slice(5)}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp: any) => (
                <tr key={emp.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ ...tdStyle, fontWeight: 600, fontSize: 13 }}>{emp.name}</td>
                  {weekDays.map((d, i) => {
                    const sh = rosterMap[`${emp.id}|${iso(d)}`];
                    return (
                      <td key={i} style={{ ...tdStyle, textAlign: "center" }}>
                        {sh ? (
                          <span title={`${sh.start_time}–${sh.end_time}`} style={{ background: sh.is_overnight ? "#ede9fe" : "#eff6ff", color: sh.is_overnight ? "#7c3aed" : NAVY, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>
                            {sh.name}
                          </span>
                        ) : <span style={{ color: "#e2e8f0" }}>—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── My upcoming shifts (employee widget) ──────────────────────────────────────
function MyShiftsWidget({ userId }: { userId: number }) {
  const start = new Date();
  const end = new Date(); end.setDate(end.getDate() + 13);
  const { data: roster = [] } = useQuery({
    queryKey: ["my-shift-roster", userId, iso(start)],
    // user_id is REQUIRED here: the roster endpoint only forces own-rows for
    // non-managers, so an admin/HR user asking without it gets the entire
    // company's assignments rendered under a "My Upcoming Shifts" heading.
    queryFn: () => getShiftRoster({ start: iso(start), end: iso(end), user_id: userId }),
    enabled: !!userId,
  });
  if ((roster as any[]).length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>My Upcoming Shifts</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(roster as any[]).slice(0, 14).map((a: any) => (
          <div key={a.date} title={a.shift ? `${a.shift.start_time}–${a.shift.end_time}` : ""}
            style={{ background: a.shift?.is_overnight ? "#ede9fe" : "#eff6ff", borderRadius: 8, padding: "6px 10px", fontSize: 11, textAlign: "center" }}>
            <div style={{ fontWeight: 700, color: a.shift?.is_overnight ? "#7c3aed" : NAVY }}>{a.shift?.name}</div>
            <div style={{ color: "#64748b" }}>{a.date.slice(5)} · {a.shift?.start_time}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Punch photo viewer (admin/hr/admin_assistant/finance + own records) ───────
type PhotoView = { userId: number; name: string; date: string; hasIn: boolean; hasOut: boolean };

function PunchPhotoModal({ view, onClose }: { view: PhotoView; onClose: () => void }) {
  const phases = [
    ...(view.hasIn ? [{ phase: "in" as const, label: "Check-in" }] : []),
    ...(view.hasOut ? [{ phase: "out" as const, label: "Check-out" }] : []),
  ];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div style={{ background: "white", borderRadius: 14, padding: 20, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: NAVY }}>📷 Punch photos{view.name ? ` — ${view.name}` : ""} · {view.date}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {phases.map(p => (
            <div key={p.phase} style={{ flex: "1 1 260px", minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>{p.label}</div>
              <img src={getPunchPhotoUrl(view.userId, view.date, p.phase)} alt={`${p.label} photo`}
                style={{ width: "100%", borderRadius: 10, border: "1px solid #e2e8f0" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Per-employee attendance detail (spec attendance-review-cleanup) ───────────
//
// Reviewing one person used to mean stepping through Mark Team one date at a
// time — a bulk-EDIT screen — because that was the only place punch photos and
// locations were rendered. Everything here comes from the existing list
// endpoint; no new API and no RBAC change.
function EmployeeAttendanceDetail({ employee, month, year, onBack, onViewPhotos }: {
  employee: { user_id: number; name: string };
  month: number; year: number;
  onBack: () => void;
  onViewPhotos: (v: PhotoView) => void;
}) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["attendance-detail", employee.user_id, month, year],
    queryFn: () => getAttendance({ user_id: employee.user_id, month, year }),
  });

  const sorted = [...(rows as any[])].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={onBack} style={{ ...primaryBtnStyle, background: "white", color: "#475569", border: "1px solid #e2e8f0", fontSize: 13 }}>
          ← Back to summary
        </button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: NAVY }}>{employee.name}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{MONTH_NAMES[month - 1]} {year} · {sorted.length} record{sorted.length === 1 ? "" : "s"}</div>
        </div>
      </div>

      {isLoading && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>}

      {!isLoading && sorted.length === 0 && (
        <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>
          No attendance records for {employee.name} in {MONTH_NAMES[month - 1]} {year}.
        </div>
      )}

      {sorted.length > 0 && (
        <div className="attendance-bulk-table" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Shift</th>
                <th style={thStyle}>In / Out</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Hours</th>
                <th style={thStyle}>Location</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Photos</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r: any) => {
                const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.present;
                const hasPhotos = r.has_check_in_photo || r.has_check_out_photo;
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ ...tdStyle, fontSize: 13, fontWeight: 600 }}>{r.date}</td>
                    <td style={tdStyle}>
                      <span style={{ background: sc.bg, color: sc.color, padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{sc.label}</span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "#475569" }}>
                      {r.shift_name || <span style={{ color: "#cbd5e1" }}>—</span>}
                      {(r.minutes_late ?? 0) > 0 && (
                        <span style={{ marginLeft: 6, background: "#fee2e2", color: "#dc2626", fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "1px 6px" }}>
                          Late {r.minutes_late}m
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "#475569" }}>
                      {r.check_in ? r.check_in.slice(0, 5) : "—"}{r.check_out ? ` – ${r.check_out.slice(0, 5)}` : ""}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", fontSize: 12 }}>{r.hours_worked ?? <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>
                      <LocationLabel record={r} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {hasPhotos ? (
                        <button
                          onClick={() => onViewPhotos({ userId: employee.user_id, name: employee.name, date: r.date, hasIn: r.has_check_in_photo, hasOut: r.has_check_out_photo })}
                          title="View punch photos"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", gap: 4 }}>
                          {r.has_check_in_photo && <img src={getPunchPhotoUrl(employee.user_id, r.date, "in")} alt="Check-in punch" style={{ height: 34, width: 34, objectFit: "cover", borderRadius: 5, border: "1px solid #e2e8f0" }} />}
                          {r.has_check_out_photo && <img src={getPunchPhotoUrl(employee.user_id, r.date, "out")} alt="Check-out punch" style={{ height: 34, width: 34, objectFit: "cover", borderRadius: 5, border: "1px solid #e2e8f0" }} />}
                        </button>
                      ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AttendancePage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const qc = useQueryClient();
  const isManager = ["admin", "admin_assistant", "hr"].includes(user?.role ?? "");

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  const [tab, setTab] = useState<ViewTab>("my");
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  // My Attendance form
  const [myForm, setMyForm] = useState({ status: "present", check_in: "", check_out: "" });

  // Manager bulk form
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [statusMap, setStatusMap] = useState<Record<number, string>>({});

  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees, enabled: isManager });

  const [summaryUserId, setSummaryUserId] = useState<string>("");

  const { data: summary = [] } = useQuery({
    queryKey: ["attendance-summary", month, year, summaryUserId],
    queryFn: () => getAttendanceSummary({ month, year, ...(summaryUserId ? { user_id: parseInt(summaryUserId) } : {}) }),
    enabled: tab === "summary",
  });

  const { data: records = [] } = useQuery({
    queryKey: ["attendance-records", month, year],
    queryFn: () => getAttendance({ month, year }),
    enabled: tab === "summary" || (tab === "my" && isManager),
  });

  const { data: myRecords = [] } = useQuery({
    queryKey: ["my-records", month, year, user?.id],
    queryFn: () => getAttendance({ user_id: user?.id, month, year }),
    enabled: tab === "my" && !!user?.id,
  });

  // Today's record pre-fill
  const { data: todayRecord } = useQuery({
    queryKey: ["my-attendance-today", todayStr, user?.id],
    queryFn: () => getAttendance({ user_id: user?.id, month: today.getMonth() + 1, year: today.getFullYear() }),
    select: (data: any[]) => data.find((r: any) => r.date === todayStr),
    enabled: tab === "my" && !!user?.id,
  });

  React.useEffect(() => {
    if (todayRecord) {
      setMyForm({
        status: todayRecord.status || "present",
        check_in: todayRecord.check_in?.slice(0, 5) || "",
        check_out: todayRecord.check_out?.slice(0, 5) || "",
      });
    }
  }, [todayRecord]);

  // Pre-fill bulk grid with existing records
  const { data: dayRecords = [] } = useQuery({
    queryKey: ["attendance-day", selectedDate],
    queryFn: () => getAttendance({ year: parseInt(selectedDate.slice(0, 4)), month: parseInt(selectedDate.slice(5, 7)) }),
    enabled: isManager && tab === "team",
    select: (data: any[]) => data.filter((r: any) => r.date === selectedDate),
  });

  // Auto-populate On Leave from approved leave requests
  const { data: onLeaveToday = [] } = useQuery({
    queryKey: ["on-leave", selectedDate],
    queryFn: () => getOnLeave(selectedDate),
    enabled: isManager && tab === "team",
  });

  React.useEffect(() => {
    if (!isManager) return;
    const map: Record<number, string> = {};
    // First fill from on-leave
    (onLeaveToday as any[]).forEach((r: any) => { map[r.user_id] = "on_leave"; });
    // Then override with actual records (more specific)
    (dayRecords as any[]).forEach((r: any) => { map[r.user_id] = r.status; });
    setStatusMap(map);
  }, [(dayRecords as any[]).length, (onLeaveToday as any[]).length, selectedDate]);

  const [punchLoading, setPunchLoading] = useState<"in" | "out" | null>(null);
  const [punchResult, setPunchResult] = useState<{ status: string; distance_m: number | null } | null>(null);
  const [cameraFor, setCameraFor] = useState<"in" | "out" | null>(null);
  const [photoView, setPhotoView] = useState<PhotoView | null>(null);
  const [detailEmployee, setDetailEmployee] = useState<{ user_id: number; name: string } | null>(null);

  const { data: attConfig } = useQuery({ queryKey: ["attendance-config"], queryFn: getAttendanceConfig });
  const requirePhoto = (attConfig as any)?.require_punch_photo === true;

  async function getGeoAndPunch(type: "in" | "out", photo?: string) {
    if (requirePhoto && !photo) {
      setCameraFor(type);  // capture first; the modal re-enters here with the photo
      return;
    }
    setPunchLoading(type);
    setPunchResult(null);
    const doCall = async (lat?: number, lng?: number, accuracy?: number) => {
      try {
        let punchRes: any = null;
        if (type === "in") {
          punchRes = await punchIn({ lat, lng, accuracy, status: myForm.status, photo });
          setPunchResult(punchRes);
        } else {
          await punchOut({ lat, lng, accuracy, photo });
        }
        qc.invalidateQueries({ queryKey: ["my-attendance-today"] });
        qc.invalidateQueries({ queryKey: ["my-records"] });
        const late = type === "in" ? (punchRes?.minutes_late ?? 0) : 0;
        toast(type === "in"
          ? (late > 0 ? `Checked in — ${late} min late for ${punchRes?.shift || "your shift"}` : "Checked in!")
          : "Checked out!", late > 0 ? "error" : "success");
      } catch (e: any) {
        toast(e?.response?.data?.detail || "Punch failed", "error");
      } finally {
        setPunchLoading(null);
      }
    };
    if (!navigator.geolocation) {
      doCall();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => doCall(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
      () => doCall(),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  const bulkMut = useMutation({
    mutationFn: bulkUpsertAttendance,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance-day"] });
      qc.invalidateQueries({ queryKey: ["attendance-summary"] });
      qc.invalidateQueries({ queryKey: ["attendance-records"] });
      toast("Attendance saved");
    },
  });

  function handleSaveBulk() {
    const recs = (employees as any[]).map((emp: any) => ({ user_id: emp.id, status: statusMap[emp.id] || "present" }));
    bulkMut.mutate({ date: selectedDate, records: recs });
  }

  function exportSummaryCSV() {
    const header = ["Employee", "Present", "Absent", "Half Day", "WFH", "On Leave", "Total"];
    const rows = (summary as any[]).map((row: any) => {
      const total = Object.keys(STATUS_CONFIG).reduce((s, k) => s + (row[k] || 0), 0);
      return [row.name, row.present || 0, row.absent || 0, row.half_day || 0, row.work_from_home || 0, row.on_leave || 0, total];
    });
    downloadCSV([header, ...rows], `attendance-summary-${year}-${String(month).padStart(2,"0")}.csv`);
  }

  function exportRecordsCSV() {
    const header = ["Employee", "Date", "Status", "Check In", "Check Out", "Hours"];
    const rows = (records as any[]).map((r: any) => [
      r.employee_name, r.date, STATUS_CONFIG[r.status]?.label || r.status,
      r.check_in || "", r.check_out || "", r.hours_worked || "",
    ]);
    downloadCSV([header, ...rows], `attendance-records-${year}-${String(month).padStart(2,"0")}.csv`);
  }

  const MONTHS = MONTH_NAMES;

  const tabs: { key: ViewTab; label: string }[] = [
    { key: "my", label: "My Attendance" },
    ...(isManager ? [
      { key: "team" as ViewTab, label: "Mark Team" },
      { key: "shifts" as ViewTab, label: "Shifts" },
      { key: "summary" as ViewTab, label: "Monthly Summary" },
      { key: "analytics" as ViewTab, label: "Analytics" },
    ] : []),
    { key: "corrections", label: "Corrections" },
  ];

  const pendingRegs = isManager
    ? 0  // loaded in CorrectionsTab
    : 0;

  return (
    <div>
      <LoadingOverlay active={bulkMut.isPending || !!punchLoading} />
      <div className="page-header">
        <div>
          <h1>Attendance</h1>
          <p>Track daily attendance and work logs</p>
        </div>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setDetailEmployee(null); }} className={tab === t.key ? "active" : ""}>{t.label}</button>
        ))}
      </div>

      {/* ── My Attendance ── */}
      {tab === "my" && (
        <div style={{ maxWidth: 600 }}>
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>Today's Attendance</h3>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
                  {formatDate(today, "long", tz)}
                </p>
              </div>
              {todayRecord && (
                <span style={{ background: STATUS_CONFIG[todayRecord.status]?.bg, color: STATUS_CONFIG[todayRecord.status]?.color, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                  Already submitted
                </span>
              )}
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Status *</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {PUNCHABLE_STATUSES.map(key => {
                  const cfg = STATUS_CONFIG[key];
                  return (
                  <button key={key} onClick={() => setMyForm(f => ({ ...f, status: key }))}
                    style={{ padding: "8px 16px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: 500,
                      border: `2px solid ${myForm.status === key ? cfg.color : "#e2e8f0"}`,
                      background: myForm.status === key ? cfg.bg : "white",
                      color: myForm.status === key ? cfg.color : "#64748b" }}>
                    {cfg.label}
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Punch buttons */}
            <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
              <button
                onClick={() => getGeoAndPunch("in")}
                disabled={!!punchLoading}
                style={{ flex: 1, padding: "12px 0", borderRadius: 8, border: "none", background: "#16a34a", color: "white", fontWeight: 700, fontSize: 15, cursor: punchLoading ? "not-allowed" : "pointer", opacity: punchLoading === "in" ? 0.7 : 1 }}
              >{punchLoading === "in" ? "Getting location…" : "📍 Check In Now"}</button>
              <button
                onClick={() => getGeoAndPunch("out")}
                // Enabled when today has no check-out yet even if it has no check-in either —
                // an overnight shift checks out the morning after; the server closes
                // yesterday's open record (and 400s clearly when nothing is open).
                disabled={!!punchLoading || !!todayRecord?.check_out}
                style={{ flex: 1, padding: "12px 0", borderRadius: 8, border: "none", background: !todayRecord?.check_out ? "#dc2626" : "#e2e8f0", color: !todayRecord?.check_out ? "white" : "#94a3b8", fontWeight: 700, fontSize: 15, cursor: (punchLoading || todayRecord?.check_out) ? "not-allowed" : "pointer", opacity: punchLoading === "out" ? 0.7 : 1 }}
              >{punchLoading === "out" ? "Getting location…" : "🏁 Check Out Now"}</button>
            </div>

            {/* Location result banner */}
            {punchResult && (
              <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: punchResult.status === "office" ? "#dcfce7" : punchResult.status === "denied" ? "#fef9c3" : "#fff7ed", border: `1px solid ${punchResult.status === "office" ? "#86efac" : punchResult.status === "denied" ? "#fde047" : "#fed7aa"}`, fontSize: 13 }}>
                {punchResult.status === "office" && "🏢 Checked in from office"}
                {punchResult.status === "outside" && `📍 Checked in ${punchResult.distance_m != null ? `${punchResult.distance_m}m from office` : "outside office"}`}
                {punchResult.status === "wfh" && "🏠 Checked in as WFH"}
                {punchResult.status === "captured" && "📍 Checked in — location captured"}
                {punchResult.status === "denied" && "⚠️ Location not shared — check-in recorded without location"}
                {punchResult.status === "manual" && "✓ Check-in recorded"}
              </div>
            )}

            {todayRecord?.check_in && (
              <div style={{ marginBottom: 14, fontSize: 13, color: "#475569", background: "#f8fafc", borderRadius: 8, padding: "8px 14px" }}>
                {todayRecord.check_in && <span>Checked in: <strong>{todayRecord.check_in}</strong></span>}
                {todayRecord.check_out && <span style={{ marginLeft: 16 }}>Checked out: <strong>{todayRecord.check_out}</strong></span>}
                {todayRecord.hours_worked != null && <span style={{ marginLeft: 16 }}>Hours: <strong>{todayRecord.hours_worked}h</strong></span>}
                {(todayRecord.check_in_lat != null || (todayRecord.location_status && todayRecord.location_status !== "manual")) && (
                  <span style={{ marginLeft: 16 }}>
                    <LocationLabel record={todayRecord} />
                  </span>
                )}
              </div>
            )}

            <MyShiftsWidget userId={user?.id || 0} />
          </div>


          {/* My Records list */}
          {(myRecords as any[]).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>This Month's Records</div>
                <button onClick={() => {
                  const header = ["Date", "Status", "Check In", "Check Out", "Hours"];
                  const rows = (myRecords as any[]).map((r: any) => [r.date, STATUS_CONFIG[r.status]?.label || r.status, r.check_in || "", r.check_out || "", r.hours_worked || ""]);
                  downloadCSV([header, ...rows], `my-attendance-${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2,"0")}.csv`);
                }} style={{ fontSize: 12, background: "white", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 10px", cursor: "pointer", color: "#475569" }}>
                  ↓ Export CSV
                </button>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {(myRecords as any[]).slice(0, 10).map((r: any) => {
                  const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.present;
                  return (
                    <div key={r.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ background: sc.bg, color: sc.color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{sc.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>{r.date}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
                        {r.check_in?.slice(0, 5)}{r.check_out ? ` – ${r.check_out.slice(0, 5)}` : ""}
                        <LocationLabel record={r} compact />
                        {(r.has_check_in_photo || r.has_check_out_photo) && (
                          <button onClick={() => setPhotoView({ userId: user?.id || 0, name: "", date: r.date, hasIn: r.has_check_in_photo, hasOut: r.has_check_out_photo })}
                            title="View punch photos" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 13 }}>📷</button>
                        )}
                        {r.shift_name && <span style={{ background: "#eff6ff", color: NAVY, fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "1px 6px" }}>{r.shift_name}</span>}
                        {(r.minutes_late ?? 0) > 0 && <span style={{ background: "#fee2e2", color: "#dc2626", fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "1px 6px" }}>Late {r.minutes_late}m</span>}
                        {r.is_manual && <span title="Manual entry" style={{ fontSize: 10, background: "#f1f5f9", color: "#94a3b8", borderRadius: 4, padding: "1px 4px" }}>manual</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Mark Team ── */}
      {tab === "team" && isManager && (
        <div>
          <WhosInToday date={selectedDate} employees={employees as any[]} />

          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <label style={labelStyle}>Date</label>
              <DateInput value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                max={todayStr} style={{ ...inputStyle, width: 180 }} />
            </div>
            <button onClick={handleSaveBulk} disabled={bulkMut.isPending} style={{ ...primaryBtnStyle, marginTop: 20 }}>
              {bulkMut.isPending ? "Saving…" : "Save Attendance"}
            </button>
          </div>

          {(onLeaveToday as any[]).length > 0 && (
            <div style={{ background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#5b21b6" }}>
              🗓 {(onLeaveToday as any[]).length} employee{(onLeaveToday as any[]).length > 1 ? "s" : ""} on approved leave — pre-filled as On Leave
            </div>
          )}

          <div className="attendance-bulk-table" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={thStyle}>Employee</th>
                  <th style={thStyle}>Department</th>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <th key={key} style={{ ...thStyle, textAlign: "center" }}>
                      <span style={{ background: cfg.bg, color: cfg.color, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{cfg.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(employees as any[]).map((emp: any) => {
                  const current = statusMap[emp.id] || "present";
                  const isOnLeave = (onLeaveToday as any[]).some((r: any) => r.user_id === emp.id);
                  const empRecord = (dayRecords as any[]).find((r: any) => r.user_id === emp.id);
                  return (
                    <tr key={emp.id} style={{ borderBottom: "1px solid #f1f5f9", background: isOnLeave ? "#faf5ff" : "white" }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{emp.name}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{emp.designation || emp.email}</div>
                        {isOnLeave && <div style={{ fontSize: 10, color: "#7c3aed", fontWeight: 600 }}>📅 On approved leave</div>}
                        {empRecord?.check_in && (
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                            {empRecord.check_in.slice(0, 5)}{empRecord.check_out ? ` – ${empRecord.check_out.slice(0, 5)}` : ""}
                            <LocationLabel record={empRecord} compact />
                            {(empRecord.has_check_in_photo || empRecord.has_check_out_photo) && (
                              <button onClick={() => setPhotoView({ userId: emp.id, name: emp.name, date: selectedDate, hasIn: empRecord.has_check_in_photo, hasOut: empRecord.has_check_out_photo })}
                                title="View punch photos" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 13 }}>📷</button>
                            )}
                            {empRecord.shift_name && <span style={{ background: "#eff6ff", color: NAVY, fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "1px 6px" }}>{empRecord.shift_name}</span>}
                            {(empRecord.minutes_late ?? 0) > 0 && <span style={{ background: "#fee2e2", color: "#dc2626", fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "1px 6px" }}>Late {empRecord.minutes_late}m</span>}
                            {empRecord.is_manual && <span style={{ fontSize: 10, background: "#f1f5f9", color: "#94a3b8", borderRadius: 4, padding: "1px 4px" }}>manual</span>}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: "#64748b", fontSize: 13 }}>{emp.department || "—"}</td>
                      {Object.keys(STATUS_CONFIG).map(key => (
                        <td key={key} style={{ ...tdStyle, textAlign: "center" }}>
                          <input type="radio" name={`status-${emp.id}`} value={key}
                            checked={current === key}
                            onChange={() => setStatusMap(m => ({ ...m, [emp.id]: key }))}
                            style={{ cursor: "pointer", width: 16, height: 16 }} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Monthly Summary ── */}
      {tab === "summary" && detailEmployee && (
        <EmployeeAttendanceDetail
          employee={detailEmployee}
          month={month}
          year={year}
          onBack={() => setDetailEmployee(null)}
          onViewPhotos={setPhotoView}
        />
      )}

      {tab === "summary" && !detailEmployee && (
        <div>
          <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label style={labelStyle}>Month</label>
              <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={{ ...inputStyle, width: 120 }}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Year</label>
              <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ ...inputStyle, width: 100 }}>
                {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Employee</label>
              <select value={summaryUserId} onChange={e => setSummaryUserId(e.target.value)} style={{ ...inputStyle, width: 200 }}>
                <option value="">All employees</option>
                {(employees as any[]).map((emp: any) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <button onClick={exportSummaryCSV} style={{ ...primaryBtnStyle, background: "white", color: "#475569", border: "1px solid #e2e8f0", fontSize: 13 }}>
              ↓ Export CSV
            </button>
          </div>

          <div className="attendance-bulk-table" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={thStyle}>Employee</th>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <th key={key} style={{ ...thStyle, textAlign: "center" }}>{cfg.label}</th>
                  ))}
                  <th style={{ ...thStyle, textAlign: "center" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(summary as any[]).map((row: any) => {
                  const total = Object.keys(STATUS_CONFIG).reduce((s, k) => s + (row[k] || 0), 0);
                  return (
                    <tr key={row.user_id}
                        onClick={() => setDetailEmployee({ user_id: row.user_id, name: row.name })}
                        title={`View ${row.name}'s punches, locations and photos`}
                        style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>{row.name}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>View punches, locations &amp; photos →</div>
                      </td>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <td key={key} style={{ ...tdStyle, textAlign: "center" }}>
                          {row[key] > 0
                            ? <span style={{ background: cfg.bg, color: cfg.color, padding: "2px 10px", borderRadius: 12, fontSize: 13, fontWeight: 600 }}>{row[key]}</span>
                            : <span style={{ color: "#cbd5e1" }}>—</span>}
                        </td>
                      ))}
                      <td style={{ ...tdStyle, textAlign: "center", fontWeight: 700, color: NAVY }}>{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(summary as any[]).length === 0 && (
              <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8" }}>No records for {MONTHS[month - 1]} {year}.</div>
            )}
          </div>
        </div>
      )}

      {/* ── Analytics ── */}
      {tab === "analytics" && isManager && <AnalyticsTab />}

      {/* ── Corrections ── */}
      {tab === "shifts" && isManager && <ShiftsTab employees={employees as any[]} />}

      {tab === "corrections" && <CorrectionsTab isManager={isManager} />}

      {cameraFor && (
        <CameraCaptureModal
          phase={cameraFor}
          onCancel={() => setCameraFor(null)}
          onCapture={photo => { const t = cameraFor; setCameraFor(null); getGeoAndPunch(t, photo); }}
        />
      )}
      {photoView && <PunchPhotoModal view={photoView} onClose={() => setPhotoView(null)} />}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
const thStyle: React.CSSProperties = {
  padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600,
  color: "#475569", borderBottom: "1px solid #e2e8f0",
};
const tdStyle: React.CSSProperties = {
  padding: "12px 14px", fontSize: 13, color: "#1e293b",
};
